if (!customElements.get("media-carousel-component")) {
  customElements.define(
    "media-carousel-component",
    class MediaCarouselComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector(".swiper-controls");
        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === "true";
        this.sectionId = this.dataset.sectionId;
        this.imageDuration = parseInt(this.dataset.imageDuration) || 3000;
        this.currentTimeout = null;
        this.currentVideoEndHandler = null;
        this.timeUpdateHandler = null;
        this.youtubeCheckInterval = null;
        this.youtubeMessageHandler = null;
        this.videoElementObserver = null;
        this.isHovered = false;
        this.isPaused = false;
        this.pausedProgress = 0; // Track progress when paused

        if (!this.enableSlider) return;

        this.initSlider();
      }

      initSlider() {
        const additionModules = [
          window.FoxTheme.Swiper.Autoplay,
          window.FoxTheme.Swiper.EffectFade,
        ];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: "fade",
          fadeEffect: {
            crossFade: true,
          },
          autoplay: false, // Disable Swiper autoplay, we'll control it manually
          on: {
            init: this.handleAfterInit.bind(this),
            slideChange: this.handleSlideChange.bind(this),
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.navigation = {
            nextEl: this.sliderControls.querySelector(".swiper-button-next"),
            prevEl: this.sliderControls.querySelector(".swiper-button-prev"),
          };
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector(".swiper-pagination"),
            clickable: true,
          };
        }

        // Setup progress bar before init (if controls exist)
        this.setupProgressBar();

        // Hide pagination controls by default
        this.hidePaginationControls();

        this.sliderInstance = new window.FoxTheme.Carousel(
          this,
          this.sliderOptions,
          additionModules
        );
        this.sliderInstance.init();

        // Setup hover handlers
        this.setupHoverHandlers();

        // Start the first slide - will be triggered after handleAfterInit
        // Don't call startCurrentSlide() here, let handleAfterInit trigger it
      }

      handleAfterInit() {
        this.removeAttribute("data-media-loading");

        // Ensure progress bar is setup
        this.setupProgressBar();

        // Fix active bullet not transition on the first time (like slideshow)
        // Use setTimeout to ensure pagination is fully rendered
        setTimeout(() => {
          if (this.sliderControls) {
            const activeBullet = this.sliderControls.querySelector(
              ".swiper-pagination-bullet-active"
            );

            if (activeBullet) {
              activeBullet.classList.remove("swiper-pagination-bullet-active");
              activeBullet.offsetHeight; // Trigger reflow
              activeBullet.classList.add("swiper-pagination-bullet-active");
            }
          }

          // Start the first slide after everything is set up
          this.warmNearbySlideImages();
          this.startCurrentSlide();
        }, 100);
      }

      warmNearbySlideImages() {
        const swiper = this.sliderInstance?.slider;
        if (!swiper) return;

        const realIndex =
          typeof swiper.realIndex === "number" ? swiper.realIndex : 0;
        const uniqueSlides = Array.from(
          this.querySelectorAll(".swiper-slide[data-index]")
        ).filter((slide, index, list) => {
          const dataIndex = slide.getAttribute("data-index");
          return (
            list.findIndex(
              (candidate) => candidate.getAttribute("data-index") === dataIndex
            ) === index
          );
        });

        if (!uniqueSlides.length) return;

        // Current + next slide (wraps with loop) — enough headroom for autoplay.
        [0, 1].forEach((offset) => {
          const targetIndex = (realIndex + offset) % uniqueSlides.length;
          const slide = uniqueSlides.find(
            (candidate) =>
              Number(candidate.getAttribute("data-index")) === targetIndex
          );
          this.eagerLoadSlideImages(slide);
        });
      }

      eagerLoadSlideImages(slide) {
        if (!slide) return;

        slide.querySelectorAll("img").forEach((img) => {
          if (img.loading === "lazy") {
            img.loading = "eager";
          }

          if (img.complete) return;

          if (typeof img.decode === "function") {
            img.decode().catch(() => {});
          }

          const warmImage = new Image();
          if (img.sizes) warmImage.sizes = img.sizes;
          if (img.srcset) warmImage.srcset = img.srcset;
          warmImage.src = img.currentSrc || img.src;
        });
      }

      setupProgressBar() {
        if (!this.sliderControls) return;

        // Set CSS variable for progress bar animation duration
        this.sliderControls.style.setProperty(
          "--swiper-autoplay-delay",
          `${this.imageDuration}ms`
        );
      }

      hidePaginationControls() {
        if (this.sliderControls) {
          this.sliderControls.style.opacity = "0";
          this.sliderControls.style.pointerEvents = "none";
          this.sliderControls.style.transition = "opacity 0.3s ease";
        }
      }

      showPaginationControls() {
        if (this.sliderControls) {
          this.sliderControls.style.opacity = "1";
          this.sliderControls.style.pointerEvents = "auto";
        }
      }

      setupHoverHandlers() {
        this.addEventListener("mouseenter", this.handleMouseEnter.bind(this));
        this.addEventListener("mouseleave", this.handleMouseLeave.bind(this));
      }

      handleMouseEnter() {
        this.isHovered = true;
        this.pauseAutoScroll();
        this.showPaginationControls();
      }

      handleMouseLeave() {
        this.isHovered = false;
        this.hidePaginationControls();
        this.resumeAutoScroll();
      }

      pauseAutoScroll() {
        if (this.isPaused) return;
        this.isPaused = true;

        // Clear timeout for image slides
        if (this.currentTimeout) {
          clearTimeout(this.currentTimeout);
          this.currentTimeout = null;
        }

        // Clear all video-related timeouts and intervals
        if (this.youtubeCheckInterval) {
          clearTimeout(this.youtubeCheckInterval);
          this.youtubeCheckInterval = null;
        }

        // Clear video end handlers to prevent them from triggering goToNextSlide
        // We'll use clearCurrentSlide which handles all cleanup
        this.clearCurrentSlide();

        // Pause video if playing
        const activeSlide =
          this.sliderInstance?.slider?.slides[
            this.sliderInstance.slider.activeIndex
          ];
        if (activeSlide) {
          const videoElement = activeSlide.querySelector(
            '[data-media-type="video"]'
          );
          if (videoElement) {
            this.pauseVideo(videoElement);
          }
        }

        // Pause progress bar animation
        this.pauseProgressBar();
      }

      resumeAutoScroll() {
        if (!this.isPaused) return;
        this.isPaused = false;

        // Resume current slide
        this.startCurrentSlide();
      }

      pauseVideo(videoElement) {
        const currentVideo = this.getCurrentDeviceVideo(videoElement);
        if (!currentVideo) return;

        const videoEl = currentVideo.querySelector("video-element");
        if (!videoEl) return;

        // Try to pause native video
        const nativeVideo = videoEl.querySelector("video");
        if (nativeVideo) {
          nativeVideo.pause();
        }

        // Try to pause via displayedMedia
        const player = window.FoxTheme?.utils?.displayedMedia?.([videoEl]);
        if (player) {
          if (player.pause && typeof player.pause === "function") {
            try {
              player.pause();
            } catch (e) {
              console.log("[MediaCarousel] pauseVideo error:", e);
            }
          } else if (
            player.pauseVideo &&
            typeof player.pauseVideo === "function"
          ) {
            // YouTube player
            try {
              player.pauseVideo();
            } catch (e) {
              console.log("[MediaCarousel] YouTube pauseVideo error:", e);
            }
          }
        }
      }

      pauseProgressBar() {
        if (!this.sliderControls) return;

        // Pause animation by setting duration to 0 (effectively pausing)
        // The actual pause is handled by CSS transition being stopped
        // We'll restore it in startProgressBarAnimation
        this.sliderControls.style.setProperty(
          "--swiper-autoplay-delay",
          "999999ms"
        );
      }

      handleSlideChange(swiper) {
        // Clear any pending timeouts/handlers
        this.clearCurrentSlide();

        this.warmNearbySlideImages();

        // Don't start new slide if paused (hovering)
        if (this.isPaused) return;

        // Start the new slide
        setTimeout(() => {
          this.startCurrentSlide();
        }, 100);
      }

      startProgressBarAnimation() {
        if (!this.sliderControls) return;

        // Restore CSS variable if it was paused
        this.sliderControls.style.setProperty(
          "--swiper-autoplay-delay",
          `${this.imageDuration}ms`
        );

        // Wait a bit for Swiper to update active bullet
        setTimeout(() => {
          const activeBullet = this.sliderControls.querySelector(
            ".swiper-pagination-bullet-active"
          );
          if (!activeBullet) return;

          // Reset animation by removing and re-adding the active class
          // This triggers the CSS transition to restart
          activeBullet.classList.remove("swiper-pagination-bullet-active");
          // Force reflow
          activeBullet.offsetHeight;
          activeBullet.classList.add("swiper-pagination-bullet-active");
        }, 50);
      }

      startCurrentSlide() {
        // Don't start if paused (hovering)
        if (this.isPaused) return;

        const activeSlide =
          this.sliderInstance.slider.slides[
            this.sliderInstance.slider.activeIndex
          ];
        if (!activeSlide) return;

        // Start progress bar animation
        this.startProgressBarAnimation();

        const mediaType = activeSlide.dataset.type;
        const imageElement = activeSlide.querySelector(
          '[data-media-type="image"]'
        );
        const videoElement = activeSlide.querySelector(
          '[data-media-type="video"]'
        );

        if (mediaType === "image" && imageElement) {
          // Block là image: hiển thị trong duration rồi chuyển slide tiếp theo
          this.currentTimeout = setTimeout(() => {
            this.goToNextSlide();
          }, this.imageDuration);
        } else if (mediaType === "video" && videoElement) {
          // Block là video: play video và chờ ended
          // Get current device video and trigger play immediately
          const currentVideo = this.getCurrentDeviceVideo(videoElement);
          if (currentVideo) {
            const videoEl = currentVideo.querySelector("video-element");
            if (videoEl) {
              // Trigger play immediately to ensure video starts
              if (videoEl.play && typeof videoEl.play === "function") {
                try {
                  videoEl.play();
                } catch (e) {
                  console.log(
                    "[MediaCarousel] startCurrentSlide: videoEl.play() error:",
                    e
                  );
                }
              }
              // Also try to trigger via displayedMedia (like slideshow does)
              const displayedVideo = window.FoxTheme?.utils?.displayedMedia?.([
                videoEl,
              ]);
              if (displayedVideo && displayedVideo.play) {
                try {
                  displayedVideo.play();
                } catch (e) {
                  console.log(
                    "[MediaCarousel] startCurrentSlide: displayedVideo.play() error:",
                    e
                  );
                }
              }
            }
          }

          this.playVideo(videoElement);
          this.waitForVideoEnd();
        } else {
          console.log(
            "[MediaCarousel] startCurrentSlide: Unknown media type or missing element",
            mediaType,
            imageElement,
            videoElement
          );
        }
      }

      playVideo(videoElement) {
        // Tìm video element cho device hiện tại
        const currentVideo = this.getCurrentDeviceVideo(videoElement);
        if (!currentVideo) {
          console.log(
            "[MediaCarousel] playVideo: No current device video found"
          );
          return;
        }

        const videoEl = currentVideo.querySelector("video-element");
        if (!videoEl) {
          console.log("[MediaCarousel] playVideo: No video-element found");
          return;
        }

        console.log(
          "[MediaCarousel] playVideo: Video element found, attempting to play"
        );

        // Try to trigger play immediately if video element has play method
        if (videoEl.play && typeof videoEl.play === "function") {
          try {
            videoEl.play();
          } catch (e) {
            console.log("[MediaCarousel] playVideo: videoEl.play() error:", e);
          }
        }

        // Đợi video element được initialize
        const tryPlay = (attempts = 0) => {
          if (attempts > 20) {
            return; // Max 10 seconds wait
          }

          // Check for native video element first
          const nativeVideo = videoEl.querySelector("video");
          if (nativeVideo) {
            nativeVideo.play().catch((e) => {
              console.log(
                "[MediaCarousel] Video autoplay blocked, will retry:",
                e
              );
            });
            return; // Video will play, exit
          }

          // Check source attribute to determine video type
          const source = videoEl.getAttribute("source");

          if (source === "youtube") {
            // YouTube - ensure video element is visible first
            const motionElement = videoEl.closest("motion-element");
            if (motionElement) {
              // Remove hidden classes to ensure video is visible
              motionElement.classList.remove("hidden");
            }

            // Try to get player from iframe or initializePlayer
            const iframe = videoEl.querySelector("iframe");
            if (iframe) {
              // Try to get player from iframe id
              if (iframe.id) {
                const ytPlayer = window[iframe.id];
                if (ytPlayer && ytPlayer.playVideo) {
                  try {
                    ytPlayer.playVideo();
                    return; // Success
                  } catch (e) {
                    console.log("[MediaCarousel] YouTube video play error:", e);
                  }
                }
              }

              // If iframe doesn't have id yet, wait for it
              if (!iframe.id && attempts < 10) {
                setTimeout(() => tryPlay(attempts + 1), 500);
                return;
              }
            }

            // Try initializePlayer
            if (videoEl.initializePlayer) {
              try {
                const playerPromise = videoEl.initializePlayer();
                if (playerPromise && playerPromise.then) {
                  playerPromise
                    .then((ytPlayer) => {
                      if (ytPlayer && ytPlayer.playVideo) {
                        try {
                          ytPlayer.playVideo();
                        } catch (e) {
                          console.log(
                            "[MediaCarousel] YouTube playVideo error:",
                            e
                          );
                        }
                      }
                    })
                    .catch((e) => {
                      console.log(
                        "[MediaCarousel] YouTube initializePlayer error:",
                        e
                      );
                    });
                  return; // Will play when promise resolves
                } else if (playerPromise && playerPromise.playVideo) {
                  try {
                    playerPromise.playVideo();
                  } catch (e) {
                    console.log("[MediaCarousel] YouTube playVideo error:", e);
                  }
                  return; // Success
                }
              } catch (e) {
                console.log(
                  "[MediaCarousel] YouTube initializePlayer error:",
                  e
                );
              }
            }

            // If autoplay is set in iframe src, video should play automatically
            // But we need to ensure iframe is visible and loaded
            if (iframe) {
              const iframeSrc = iframe.getAttribute("src");
              if (iframeSrc && iframeSrc.includes("autoplay=1")) {
                // Wait a bit for iframe to load and play
                if (attempts < 5) {
                  setTimeout(() => tryPlay(attempts + 1), 1000);
                  return;
                }
              }
            }

            setTimeout(() => tryPlay(attempts + 1), 500);
          } else if (source === "vimeo") {
            // Vimeo - try to get player from displayedMedia or initializePlayer
            const player = window.FoxTheme.utils.displayedMedia([videoEl]);
            if (player && player.play && typeof player.play === "function") {
              player.play().catch(() => {
                console.log("[MediaCarousel] Vimeo video autoplay blocked");
              });
              return; // Success
            }

            // Try initializePlayer
            if (videoEl.initializePlayer) {
              try {
                const playerPromise = videoEl.initializePlayer();
                if (playerPromise && playerPromise.then) {
                  playerPromise
                    .then((vimeoPlayer) => {
                      if (vimeoPlayer && vimeoPlayer.play) {
                        vimeoPlayer.play();
                      }
                    })
                    .catch((e) => {
                      console.log(
                        "[MediaCarousel] Vimeo initializePlayer error:",
                        e
                      );
                    });
                  return; // Will play when promise resolves
                } else if (playerPromise && playerPromise.play) {
                  playerPromise.play();
                  return; // Success
                }
              } catch (e) {
                console.log("[MediaCarousel] Vimeo initializePlayer error:", e);
              }
            }

            setTimeout(() => tryPlay(attempts + 1), 500);
          } else {
            // Native video or unknown - try displayedMedia
            const player = window.FoxTheme.utils.displayedMedia([videoEl]);
            if (player) {
              if (player.playVideo && typeof player.playVideo === "function") {
                // YouTube player (fallback)
                try {
                  player.playVideo();
                  return;
                } catch (e) {
                  console.log("[MediaCarousel] YouTube video play error:", e);
                }
              } else if (player.play && typeof player.play === "function") {
                // Vimeo or native video player
                player.play().catch(() => {
                  console.log("[MediaCarousel] Video autoplay blocked");
                });
                return;
              }
            }

            setTimeout(() => tryPlay(attempts + 1), 500);
          }
        };
        setTimeout(() => tryPlay(), 300);
      }

      getCurrentDeviceVideo(videoElement) {
        // Xác định device hiện tại và trả về video element tương ứng
        const width = window.innerWidth;
        let device = "desktop";
        let selector = '[data-video-device="desktop"]';

        // Match Tailwind breakpoints: md=768px, lg=1024px
        if (width >= 1024) {
          // Desktop (lg and above)
          device = "desktop";
          selector = '[data-video-device="desktop"]';
        } else if (width >= 768) {
          // Tablet (md to lg-1, i.e., 768px to 1023px)
          device = "tablet";
          selector = '[data-video-device="tablet"]';
        } else {
          // Mobile (below md, i.e., < 768px)
          device = "mobile";
          selector = '[data-video-device="mobile"]';
        }

        // Try to get video for current device
        let currentVideo = videoElement.querySelector(selector);

        // If not found, try fallback: desktop -> tablet -> mobile
        if (!currentVideo) {
          // Try desktop first
          currentVideo = videoElement.querySelector(
            '[data-video-device="desktop"]'
          );
          if (!currentVideo) {
            // Try tablet
            currentVideo = videoElement.querySelector(
              '[data-video-device="tablet"]'
            );
            if (!currentVideo) {
              // Try mobile
              currentVideo = videoElement.querySelector(
                '[data-video-device="mobile"]'
              );
            }
          }
        }

        return currentVideo;
      }

      waitForVideoEnd() {
        const activeSlide =
          this.sliderInstance.slider.slides[
            this.sliderInstance.slider.activeIndex
          ];
        if (!activeSlide) {
          return;
        }

        const videoElement = activeSlide.querySelector(
          '[data-media-type="video"]'
        );
        if (!videoElement) {
          return;
        }

        const currentVideo = this.getCurrentDeviceVideo(videoElement);
        if (!currentVideo) {
          return;
        }

        const videoEl = currentVideo.querySelector("video-element");
        if (!videoEl) {
          return;
        }

        // Also listen to video-element for ended state via attribute observer
        // This works for all video types (native, YouTube, Vimeo)
        const observeVideoElement = () => {
          let wasPlaying = videoEl.hasAttribute("playing");
          let videoStartedPlaying = false;

          // Check if already playing
          if (wasPlaying) {
            videoStartedPlaying = true;
            // Video already playing, set timeout to auto-advance after reasonable time
            const VIDEO_TIMEOUT = 300000; // 5 minutes default (videos can be long)
            if (this.youtubeCheckInterval) {
              clearTimeout(this.youtubeCheckInterval);
            }
            this.youtubeCheckInterval = setTimeout(() => {
              if (this.youtubeCheckInterval) {
                clearTimeout(this.youtubeCheckInterval);
                this.youtubeCheckInterval = null;
              }
              observer.disconnect();
              this.videoElementObserver = null;
              this.goToNextSlide();
            }, VIDEO_TIMEOUT);
          }

          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (
                mutation.type === "attributes" &&
                mutation.attributeName === "playing"
              ) {
                const isPlaying = videoEl.hasAttribute("playing");

                // Video started playing
                if (!wasPlaying && isPlaying) {
                  videoStartedPlaying = true;
                  // Set timeout to auto-advance after reasonable time (5 minutes)
                  // This ensures carousel doesn't get stuck even if ended event doesn't fire
                  const VIDEO_TIMEOUT = 300000; // 5 minutes (videos can be long)
                  if (this.youtubeCheckInterval) {
                    clearTimeout(this.youtubeCheckInterval);
                  }
                  this.youtubeCheckInterval = setTimeout(() => {
                    if (this.youtubeCheckInterval) {
                      clearTimeout(this.youtubeCheckInterval);
                      this.youtubeCheckInterval = null;
                    }
                    observer.disconnect();
                    this.videoElementObserver = null;
                    this.goToNextSlide();
                  }, VIDEO_TIMEOUT);
                }

                // When playing attribute is removed after it was playing, video ended
                if (wasPlaying && !isPlaying) {
                  // Double check with a small delay to ensure it's really ended
                  setTimeout(() => {
                    if (!videoEl.hasAttribute("playing")) {
                      observer.disconnect();
                      this.videoElementObserver = null;
                      // Clear YouTube timeout if exists
                      if (this.youtubeCheckInterval) {
                        clearTimeout(this.youtubeCheckInterval);
                        this.youtubeCheckInterval = null;
                      }
                      this.goToNextSlide();
                    }
                    wasPlaying = isPlaying;
                  }, 100);
                } else {
                  wasPlaying = isPlaying;
                }
              }
            });
          });
          observer.observe(videoEl, {
            attributes: true,
            attributeFilter: ["playing"],
          });
          this.videoElementObserver = observer;
        };
        observeVideoElement();

        // Đợi player được initialize và setup event listener
        const setupEndListener = (attempts = 0) => {
          if (attempts > 20) {
            return; // Max 10 seconds wait
          }

          const player = window.FoxTheme.utils.displayedMedia([videoEl]);
          if (player) {
            // Handle different video types
            // First check if it's a native video element inside video-element
            const nativeVideo = videoEl.querySelector("video");
            if (nativeVideo) {
              // Native video element
              this.currentVideoEndHandler = () => {
                this.goToNextSlide();
              };
              nativeVideo.addEventListener(
                "ended",
                this.currentVideoEndHandler
              );
              // Also listen to timeupdate to check if video ended
              this.timeUpdateHandler = () => {
                if (nativeVideo.ended) {
                  nativeVideo.removeEventListener(
                    "timeupdate",
                    this.timeUpdateHandler
                  );
                  this.goToNextSlide();
                }
              };
              nativeVideo.addEventListener(
                "timeupdate",
                this.timeUpdateHandler
              );
            } else if (
              player.addEventListener &&
              typeof player.addEventListener === "function" &&
              player.tagName === "VIDEO"
            ) {
              // Direct native video element (not wrapped in video-element)
              this.currentVideoEndHandler = () => {
                this.goToNextSlide();
              };
              player.addEventListener("ended", this.currentVideoEndHandler);
              // Also listen to timeupdate to check if video ended
              this.timeUpdateHandler = () => {
                if (player.ended) {
                  player.removeEventListener(
                    "timeupdate",
                    this.timeUpdateHandler
                  );
                  this.goToNextSlide();
                }
              };
              player.addEventListener("timeupdate", this.timeUpdateHandler);
            } else {
              // Check if it's YouTube or Vimeo by checking source attribute
              const source = videoEl.getAttribute("source");

              if (source === "youtube") {
                // YouTube video - use postMessage listener + MutationObserver + timeout fallback

                const iframe = videoEl.querySelector("iframe");
                if (iframe) {
                  // Listen to postMessage from YouTube iframe
                  this.youtubeMessageHandler = (event) => {
                    // YouTube sends messages with data containing event info
                    if (
                      event.origin === "https://www.youtube.com" &&
                      event.data &&
                      typeof event.data === "string"
                    ) {
                      try {
                        const data = JSON.parse(event.data);
                        if (data.event === "onStateChange") {
                          // State 0 = ENDED
                          if (data.info === 0) {
                            if (this.youtubeMessageHandler) {
                              window.removeEventListener(
                                "message",
                                this.youtubeMessageHandler
                              );
                              this.youtubeMessageHandler = null;
                            }
                            if (this.youtubeCheckInterval) {
                              clearTimeout(this.youtubeCheckInterval);
                              this.youtubeCheckInterval = null;
                            }
                            this.goToNextSlide();
                          }
                        }
                      } catch (e) {
                        // Not JSON, ignore
                      }
                    }
                  };
                  window.addEventListener(
                    "message",
                    this.youtubeMessageHandler
                  );
                }

                // Set a timeout fallback (30 seconds) - will be extended if video is playing
                // This ensures carousel doesn't get stuck
                let timeoutDuration = 30000; // Start with 30 seconds
                const setYouTubeTimeout = () => {
                  if (this.youtubeCheckInterval) {
                    clearTimeout(this.youtubeCheckInterval);
                  }
                  this.youtubeCheckInterval = setTimeout(() => {
                    // Check if video is still playing
                    const isPlaying = videoEl.hasAttribute("playing");
                    if (isPlaying) {
                      // Video still playing, extend timeout by another 30 seconds
                      timeoutDuration = 30000;
                      setYouTubeTimeout();
                    } else {
                      // Video not playing, move to next slide
                      if (this.youtubeCheckInterval) {
                        clearTimeout(this.youtubeCheckInterval);
                        this.youtubeCheckInterval = null;
                      }
                      this.goToNextSlide();
                    }
                  }, timeoutDuration);
                };
                setYouTubeTimeout();

                // MutationObserver (already setup above) will also handle video end detection
                // When video ends, it will call goToNextSlide() and clear this timeout
              } else if (source === "vimeo") {
                // Vimeo player - get player from video-element's initializePlayer
                const getVimeoPlayer = async () => {
                  try {
                    let vimeoPlayer = null;

                    // Try to get player from video-element's initializePlayer
                    if (videoEl.initializePlayer) {
                      try {
                        const playerPromise = videoEl.initializePlayer();
                        if (playerPromise && playerPromise.then) {
                          vimeoPlayer = await playerPromise;
                        } else if (playerPromise && playerPromise.on) {
                          vimeoPlayer = playerPromise;
                        }
                      } catch (e) {}
                    }

                    if (vimeoPlayer && vimeoPlayer.on) {
                      this.currentVideoEndHandler = () => {
                        this.goToNextSlide();
                      };
                      vimeoPlayer.on("ended", this.currentVideoEndHandler);
                    }
                  } catch (e) {
                    console.log("[MediaCarousel] Vimeo setup error:", e);
                  }
                };

                getVimeoPlayer();
              } else {
                console.log(
                  "[MediaCarousel] waitForVideoEnd: Unknown player type",
                  player
                );
              }
            }
          } else {
            // Player not ready yet, try again
            setTimeout(() => setupEndListener(attempts + 1), 500);
          }
        };

        setTimeout(() => setupEndListener(), 500);
      }

      goToNextSlide() {
        // Don't go to next slide if paused (hovering)
        if (this.isPaused) return;

        this.clearCurrentSlide();
        this.sliderInstance.slider.slideNext();
      }

      clearCurrentSlide() {
        if (this.currentTimeout) {
          clearTimeout(this.currentTimeout);
          this.currentTimeout = null;
        }

        // Clear YouTube polling interval
        if (this.youtubeCheckInterval) {
          clearTimeout(this.youtubeCheckInterval);
          this.youtubeCheckInterval = null;
        }

        // Remove YouTube message handler
        if (this.youtubeMessageHandler) {
          window.removeEventListener("message", this.youtubeMessageHandler);
          this.youtubeMessageHandler = null;
        }

        // Disconnect video element observer
        if (this.videoElementObserver) {
          this.videoElementObserver.disconnect();
          this.videoElementObserver = null;
        }

        if (this.currentVideoEndHandler) {
          const activeSlide =
            this.sliderInstance.slider.slides[
              this.sliderInstance.slider.activeIndex
            ];
          if (activeSlide) {
            const videoElement = activeSlide.querySelector(
              '[data-media-type="video"]'
            );
            if (videoElement) {
              const currentVideo = this.getCurrentDeviceVideo(videoElement);
              if (currentVideo) {
                const videoEl = currentVideo.querySelector("video-element");
                if (videoEl) {
                  const player = window.FoxTheme.utils.displayedMedia([
                    videoEl,
                  ]);
                  if (videoEl) {
                    // Check for native video element
                    const nativeVideo = videoEl.querySelector("video");
                    if (nativeVideo) {
                      if (this.currentVideoEndHandler) {
                        nativeVideo.removeEventListener(
                          "ended",
                          this.currentVideoEndHandler
                        );
                      }
                      if (this.timeUpdateHandler) {
                        nativeVideo.removeEventListener(
                          "timeupdate",
                          this.timeUpdateHandler
                        );
                      }
                    }
                  }

                  if (player) {
                    if (
                      player.removeEventListener &&
                      typeof player.removeEventListener === "function" &&
                      player.tagName === "VIDEO"
                    ) {
                      if (this.currentVideoEndHandler) {
                        player.removeEventListener(
                          "ended",
                          this.currentVideoEndHandler
                        );
                      }
                      if (this.timeUpdateHandler) {
                        player.removeEventListener(
                          "timeupdate",
                          this.timeUpdateHandler
                        );
                      }
                    } else if (player.off && typeof player.off === "function") {
                      // Vimeo player
                      player.off("ended", this.currentVideoEndHandler);
                    }
                  }
                }
              }
            }
          }
          this.currentVideoEndHandler = null;
        }
      }
    }
  );
}
